import { useState, useRef, useEffect, useCallback } from "react";
import { endVoiceCall, postVoiceAuthPromote, postVoiceEvent, mintVoiceSession, loadPriorThreadMessages, streamVoiceEventChannel, proxyVoiceTool } from "../../api.js";
import { useVoiceRecorder } from "./useVoiceRecorder.js";
import { useWebRtcStats } from "./useWebRtcStats.js";
import { VoiceBargeInController, buildServerBargeInFallbackSessionUpdate } from "./useVoiceBargeIn.js";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
async function resolveAuthHeaders() {
  try {
    return await getHeadersWithUniqueVisitorId();
  } catch {
    return {};
  }
}
const HEARTBEAT_INTERVAL_MS = 5e3;
const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const END_CALL_DRAIN_MS = 2e3;
const CONTEXT_AWARE_ACK_INSTRUCTIONS = [
  "The shopper just opened a voice call while we were chatting in text. The",
  "prior conversation has been seeded into your context. This call is just",
  'BEGINNING — never say goodbye, "bye", or anything that ends the call, even',
  "if the prior conversation ended with a farewell. Do NOT call end_call now.",
  "In ONE short sentence (max 20 words), acknowledge the resume and offer to",
  "keep going. Do not greet by name. Do not list capabilities. Do not summarize",
  "the prior conversation. Examples (tone, not literal):",
  '"Picking up where we left off — what would you like next?"',
  '"Glad to hear from you — what can I do?"',
  `"I'm here — what's the next thing?"`
].join("\n");
const PRIOR_THREAD_REPLAY_LIMIT = 20;
const PRIOR_THREAD_TOOL_REPLAY_LIMIT = 3;
const TOOLS_EXCLUDED_FROM_SFX_LOOP = /* @__PURE__ */ new Set([
  "end_call",
  "prompt_login",
  "submit_phone_for_login",
  "submit_otp_for_login"
]);
function useVoiceSession(params) {
  const { conversationId, pdpStacklineSku, onEnded, onEndCallInitiated } = params;
  const [phase, setPhase] = useState("idle");
  const [muted, setMutedState] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [lines, setLines] = useState([]);
  const linesRef = useRef([]);
  linesRef.current = lines;
  const [error, setError] = useState(void 0);
  const [errorTitle, setErrorTitle] = useState(void 0);
  const [attemptNonce, setAttemptNonce] = useState(0);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isAssistantAudible, setIsAssistantAudible] = useState(false);
  const isAssistantAudibleRef = useRef(false);
  isAssistantAudibleRef.current = isAssistantAudible;
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [toolCallsInFlight, setToolCallsInFlight] = useState(0);
  const [lastWidgetEvent, setLastWidgetEvent] = useState(void 0);
  const [widgetEvents, setWidgetEvents] = useState([]);
  const widgetEventsRef = useRef([]);
  widgetEventsRef.current = widgetEvents;
  const widgetSeqRef = useRef(0);
  const [lastStatusEvent, setLastStatusEvent] = useState(void 0);
  const statusClearTimerRef = useRef(void 0);
  const loginContextRef = useRef({});
  const [pc, setPc] = useState(null);
  const [userStream, setUserStream] = useState(null);
  const [assistantStream, setAssistantStream] = useState(null);
  const [liveStartMs, setLiveStartMs] = useState(void 0);
  const activeAttemptRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onEndCallInitiatedRef = useRef(onEndCallInitiated);
  onEndCallInitiatedRef.current = onEndCallInitiated;
  const authHeadersRef = useRef({});
  useEffect(() => {
    let cancelled = false;
    resolveAuthHeaders().then((h) => {
      if (!cancelled) authHeadersRef.current = h;
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const mutedRef = useRef(false);
  mutedRef.current = muted;
  const partialRef = useRef({});
  const safeSendDataChannel = useCallback((dc, event) => {
    if (!dc || dc.readyState !== "open") return;
    try {
      dc.send(JSON.stringify(event));
      logVoiceEvent("BROWSER", typeof (event == null ? void 0 : event.type) === "string" ? event.type : "unknown", event);
    } catch {
    }
  }, []);
  const sendOrQueueResponseCreate = useCallback(
    (attempt, payload) => {
      if (!attempt.dc || attempt.dc.readyState !== "open") return;
      if (!attempt.responseInFlight) {
        safeSendDataChannel(attempt.dc, payload);
        return;
      }
      if (attempt.pendingResponseCreate) {
        clearTimeout(attempt.pendingResponseCreate.timer);
      }
      const timer = setTimeout(() => {
        var _a;
        if (((_a = attempt.pendingResponseCreate) == null ? void 0 : _a.timer) !== timer) return;
        attempt.pendingResponseCreate = void 0;
        if (attempt.dc && attempt.dc.readyState === "open") {
          safeSendDataChannel(attempt.dc, payload);
        }
      }, 6e3);
      attempt.pendingResponseCreate = { payload, timer };
    },
    [safeSendDataChannel]
  );
  const upsertLine = useCallback((role, text, opts) => {
    const slot = partialRef.current[role];
    const targetId = slot ? slot.id : `${role}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.id === targetId);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], text, partial: opts.partial };
        return next;
      }
      const line = {
        id: targetId,
        role,
        text,
        partial: opts.partial,
        createdAt: Date.now()
      };
      return [...prev, line];
    });
    if (opts.partial) {
      partialRef.current[role] = { id: targetId, text };
    } else {
      partialRef.current[role] = void 0;
    }
  }, []);
  const teardownAttempt = useCallback(
    async (attempt, reason, opts) => {
      var _a, _b, _c, _d, _e, _f;
      if (attempt.closing) return;
      attempt.closing = true;
      attempt.cancelled = true;
      if (statusClearTimerRef.current !== void 0) {
        clearTimeout(statusClearTimerRef.current);
        statusClearTimerRef.current = void 0;
      }
      if (attempt.pendingEndTimer !== void 0) {
        clearTimeout(attempt.pendingEndTimer);
        attempt.pendingEndTimer = void 0;
      }
      attempt.pendingEndAfterMs = void 0;
      attempt.pendingEndReason = void 0;
      if (attempt.pendingResponseCreate) {
        clearTimeout(attempt.pendingResponseCreate.timer);
        attempt.pendingResponseCreate = void 0;
      }
      attempt.responseInFlight = false;
      setIsAssistantAudible(false);
      if (attempt.bargeIn) {
        try {
          await attempt.bargeIn.stop();
        } catch {
        }
        attempt.bargeIn = void 0;
      }
      try {
        if (attempt.heartbeat) clearInterval(attempt.heartbeat);
        if (attempt.ticker) clearInterval(attempt.ticker);
      } catch {
      }
      attempt.heartbeat = void 0;
      attempt.ticker = void 0;
      try {
        (_a = attempt.eventSource) == null ? void 0 : _a.close();
      } catch {
      }
      attempt.eventSource = void 0;
      try {
        (_b = attempt.dc) == null ? void 0 : _b.close();
      } catch {
      }
      attempt.dc = void 0;
      try {
        (_c = attempt.pc) == null ? void 0 : _c.getSenders().forEach((s) => {
          var _a2;
          try {
            (_a2 = s.track) == null ? void 0 : _a2.stop();
          } catch {
          }
        });
        (_d = attempt.pc) == null ? void 0 : _d.close();
      } catch {
      }
      attempt.pc = void 0;
      try {
        (_e = attempt.stream) == null ? void 0 : _e.getTracks().forEach((t) => t.stop());
      } catch {
      }
      attempt.stream = void 0;
      attempt.assistantStream = void 0;
      try {
        if (attempt.audioEl) {
          attempt.audioEl.srcObject = null;
        }
      } catch {
      }
      attempt.audioEl = void 0;
      if (activeAttemptRef.current === attempt) {
        activeAttemptRef.current = null;
        setPc(null);
        setUserStream(null);
        setAssistantStream(null);
        setLiveStartMs(void 0);
      }
      if (opts.notifyOnEnded) {
        setPhase("ended");
        const durationSec = attempt.liveStartMs != null ? Math.max(0, Math.round((Date.now() - attempt.liveStartMs) / 1e3)) : 0;
        (_f = onEndedRef.current) == null ? void 0 : _f.call(onEndedRef, {
          reason,
          durationSec,
          // mastraThreadCreated is authoritatively reconciled server-side; the
          // close handler doesn't consume it, so we omit it on the optimistic
          // path rather than guess.
          lines: linesRef.current,
          widgetEvents: widgetEventsRef.current
        });
      }
      if (opts.postEnd && attempt.minted) {
        void endVoiceCall({ conversationId, reason, headersToInject: authHeadersRef.current }).catch(() => {
        });
      }
    },
    [conversationId]
  );
  useEffect(() => {
    if (!conversationId) return;
    const attempt = {
      cancelled: false,
      minted: false,
      reachedLive: false,
      closing: false,
      responseInFlight: false,
      responseIdAtItemSpeechStart: /* @__PURE__ */ new Map(),
      suppressedResponseIds: /* @__PURE__ */ new Set()
    };
    activeAttemptRef.current = attempt;
    const fail = (err) => {
      if (attempt.cancelled) return true;
      const message = err instanceof Error ? err.message : String(err ?? "Voice mode failed");
      setErrorTitle("Voice mode unavailable");
      setError(message);
      setPhase("error");
      void teardownAttempt(attempt, "error", { postEnd: attempt.minted, notifyOnEnded: false });
      return true;
    };
    const handleFunctionCall = async (call) => {
      var _a, _b;
      let prettyArgs = call.arguments;
      try {
        prettyArgs = typeof call.arguments === "string" ? JSON.parse(call.arguments) : call.arguments;
      } catch {
      }
      if (call.name === "end_call") {
        logVoiceEvent("BROWSER", `tool request - ${call.name} (async)`, {
          conversationId,
          callId: call.call_id,
          args: prettyArgs
        });
        void proxyVoiceTool({
          conversationId,
          callId: call.call_id,
          toolName: call.name,
          args: call.arguments,
          headersToInject: authHeadersRef.current
        }).then((result) => {
          logVoiceEvent("BACKEND", `tool response - ${call.name} (async)`, result);
        }).catch((err) => {
          logVoiceEvent("BACKEND", `tool response - ${call.name} (async)`, {
            status: "error",
            error: err instanceof Error ? err.message : String(err)
          });
        });
        if (attempt.closing || attempt.cancelled) return;
        (_a = onEndCallInitiatedRef.current) == null ? void 0 : _a.call(onEndCallInitiatedRef);
        setPhase("ending");
        try {
          (_b = attempt.stream) == null ? void 0 : _b.getAudioTracks().forEach((t) => t.enabled = false);
        } catch {
        }
        if (attempt.pendingEndTimer !== void 0) clearTimeout(attempt.pendingEndTimer);
        attempt.pendingEndTimer = setTimeout(() => {
          attempt.pendingEndTimer = void 0;
          if (!attempt.cancelled && !attempt.closing) {
            void teardownAttempt(attempt, "ended_by_model", { postEnd: true, notifyOnEnded: true });
          }
        }, END_CALL_DRAIN_MS);
        return;
      }
      logVoiceEvent("BROWSER", `tool request - ${call.name}`, {
        conversationId,
        callId: call.call_id,
        args: prettyArgs
      });
      const countsForSfxLoop = !TOOLS_EXCLUDED_FROM_SFX_LOOP.has(call.name);
      if (countsForSfxLoop) {
        setToolCallsInFlight((n) => n + 1);
      }
      try {
        const result = await proxyVoiceTool({
          conversationId,
          callId: call.call_id,
          toolName: call.name,
          args: call.arguments,
          headersToInject: authHeadersRef.current
        });
        logVoiceEvent("BACKEND", `tool response - ${call.name}`, result);
        if (typeof result.endCallAfterMs === "number" && result.endCallAfterMs > 0) {
          attempt.pendingEndAfterMs = result.endCallAfterMs;
          attempt.pendingEndReason = typeof result.endCallReason === "string" && result.endCallReason.length > 0 ? result.endCallReason : void 0;
        }
        if (Array.isArray(result.widgets) && result.widgets.length > 0) {
          for (const w of result.widgets) {
            const widgetType = `data-widget-${w.type}`;
            setLastWidgetEvent({ type: widgetType, data: w.data });
            if (w.type === "clear") {
              setWidgetEvents([]);
            } else {
              const seq = ++widgetSeqRef.current;
              const evt = {
                id: `${widgetType}:${seq}`,
                type: widgetType,
                data: w.data,
                createdAt: Date.now()
              };
              setWidgetEvents((prev) => [...prev, evt]);
            }
            if (w.type === "prompt-login" && !loginContextRef.current.loginGateMs) {
              const triggeredByPromptLoginTool = call.name === "prompt_login";
              loginContextRef.current = {
                loginGateMs: Date.now(),
                blockedToolName: triggeredByPromptLoginTool ? void 0 : call.name
              };
            }
          }
          if (statusClearTimerRef.current !== void 0) {
            clearTimeout(statusClearTimerRef.current);
            statusClearTimerRef.current = void 0;
          }
          setLastStatusEvent(void 0);
        }
        safeSendDataChannel(attempt.dc, {
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: call.call_id,
            output: result.narratorText ?? ""
          }
        });
        let parsedArgs;
        try {
          parsedArgs = typeof call.arguments === "string" ? JSON.parse(call.arguments) : call.arguments;
        } catch {
          parsedArgs = void 0;
        }
        const isOtpConfirm = call.name === "submit_otp_for_login" && (parsedArgs == null ? void 0 : parsedArgs.confirmed) === true;
        if (isOtpConfirm) {
          sendOrQueueResponseCreate(attempt, {
            type: "response.create",
            response: {
              instructions: "Say ONLY 'One moment — verifying that code…' (or a brief equivalent, ≤ 6 words) and then STOP. Do NOT call any tools. Do NOT speak further until a system instruction tells you the user is signed in.",
              tool_choice: "none"
            }
          });
        } else {
          sendOrQueueResponseCreate(attempt, { type: "response.create" });
        }
      } catch (err) {
        logVoiceEvent("BACKEND", `tool response - ${call.name}`, {
          status: "error",
          error: err instanceof Error ? err.message : String(err)
        });
        console.warn("[voice] tool proxy failed", err);
        safeSendDataChannel(attempt.dc, {
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: call.call_id,
            output: "Tool unavailable. Apologize and offer an alternative."
          }
        });
        sendOrQueueResponseCreate(attempt, { type: "response.create" });
      } finally {
        if (countsForSfxLoop) {
          setToolCallsInFlight((n) => Math.max(0, n - 1));
        }
      }
    };
    const KEY_EVENTS = /* @__PURE__ */ new Set([
      "session.created",
      "session.updated",
      "response.created",
      "response.done",
      "conversation.item.input_audio_transcription.completed",
      "response.audio_transcript.done",
      "response.output_audio_transcript.done",
      "output_audio_buffer.started",
      "output_audio_buffer.stopped",
      "input_audio_buffer.speech_started",
      "input_audio_buffer.speech_stopped",
      "error"
    ]);
    const handleServerEvent = async (event) => {
      var _a, _b, _c, _d, _e, _f, _g;
      if (!event || typeof event !== "object" || typeof event.type !== "string") return;
      if (attempt.cancelled) return;
      if (KEY_EVENTS.has(event.type)) {
        postVoiceEvent({ conversationId, events: [event], headersToInject: authHeadersRef.current });
      }
      switch (event.type) {
        case "input_audio_buffer.speech_started":
          setIsUserSpeaking(true);
          if (typeof event.item_id === "string") {
            attempt.responseIdAtItemSpeechStart.set(event.item_id, attempt.activeResponseId);
          }
          if (attempt.pendingEndTimer !== void 0) {
            clearTimeout(attempt.pendingEndTimer);
            attempt.pendingEndTimer = void 0;
          }
          attempt.pendingEndAfterMs = void 0;
          attempt.pendingEndReason = void 0;
          break;
        case "input_audio_buffer.speech_stopped":
          setIsUserSpeaking(false);
          break;
        case "output_audio_buffer.started":
          setIsAssistantAudible(true);
          break;
        case "output_audio_buffer.stopped":
          setIsAssistantAudible(false);
          break;
        case "response.created":
          setIsAssistantSpeaking(true);
          attempt.responseInFlight = true;
          attempt.activeResponseId = typeof ((_a = event.response) == null ? void 0 : _a.id) === "string" ? event.response.id : void 0;
          if (partialRef.current.assistant) {
            const stranded = partialRef.current.assistant;
            upsertLine("assistant", stranded.text, { partial: false });
          }
          break;
        case "response.done": {
          setIsAssistantSpeaking(false);
          attempt.responseInFlight = false;
          attempt.activeResponseId = void 0;
          const pending = attempt.pendingResponseCreate;
          if (pending && ((_b = attempt.dc) == null ? void 0 : _b.readyState) === "open") {
            clearTimeout(pending.timer);
            attempt.pendingResponseCreate = void 0;
            safeSendDataChannel(attempt.dc, pending.payload);
          }
          const status = (_c = event.response) == null ? void 0 : _c.status;
          if (status && status !== "completed" && partialRef.current.assistant) {
            const stranded = partialRef.current.assistant;
            upsertLine("assistant", stranded.text, { partial: false });
          }
          if (status && status !== "completed") {
            setIsAssistantAudible(false);
          }
          const doneResponseId = typeof ((_d = event.response) == null ? void 0 : _d.id) === "string" ? event.response.id : void 0;
          if (doneResponseId && attempt.suppressedResponseIds.has(doneResponseId)) {
            attempt.suppressedResponseIds.delete(doneResponseId);
            break;
          }
          const items = ((_e = event.response) == null ? void 0 : _e.output) ?? [];
          const calls = items.filter((it) => (it == null ? void 0 : it.type) === "function_call");
          for (const call of calls) {
            await handleFunctionCall(call);
          }
          if (attempt.pendingEndAfterMs !== void 0 && attempt.pendingEndAfterMs > 0 && calls.length === 0) {
            const delayMs = attempt.pendingEndAfterMs;
            const reason = attempt.pendingEndReason ?? "call_ended_by_tool";
            attempt.pendingEndAfterMs = void 0;
            attempt.pendingEndReason = void 0;
            attempt.pendingEndTimer = setTimeout(() => {
              attempt.pendingEndTimer = void 0;
              if (!attempt.cancelled && !attempt.closing) {
                void teardownAttempt(attempt, reason, { postEnd: true, notifyOnEnded: true });
              }
            }, delayMs);
          }
          break;
        }
        case "conversation.item.input_audio_transcription.delta": {
          if (typeof event.delta === "string") {
            const prev = ((_f = partialRef.current.user) == null ? void 0 : _f.text) ?? "";
            upsertLine("user", prev + event.delta, { partial: true });
          }
          break;
        }
        case "conversation.item.input_audio_transcription.completed": {
          const transcript = typeof event.transcript === "string" ? event.transcript : "";
          const trimmed = transcript.trim();
          if (!trimmed) {
            const itemId = typeof event.item_id === "string" ? event.item_id : void 0;
            const responseAtSpeechStart = itemId ? attempt.responseIdAtItemSpeechStart.get(itemId) : void 0;
            if (itemId) attempt.responseIdAtItemSpeechStart.delete(itemId);
            const activeId = attempt.activeResponseId;
            const noiseTriggeredResponse = !!activeId && activeId !== responseAtSpeechStart;
            if (noiseTriggeredResponse && activeId) {
              safeSendDataChannel(attempt.dc, { type: "response.cancel" });
              attempt.suppressedResponseIds.add(activeId);
            }
            if (partialRef.current.user) {
              setLines((prev) => prev.filter((l) => l.id !== partialRef.current.user.id));
              partialRef.current.user = void 0;
            }
            break;
          }
          upsertLine("user", trimmed, { partial: false });
          break;
        }
        case "response.audio_transcript.delta":
        case "response.output_audio_transcript.delta": {
          if (typeof event.delta === "string") {
            const prev = ((_g = partialRef.current.assistant) == null ? void 0 : _g.text) ?? "";
            upsertLine("assistant", prev + event.delta, { partial: true });
          }
          break;
        }
        case "response.audio_transcript.done":
        case "response.output_audio_transcript.done": {
          if (typeof event.transcript === "string") {
            upsertLine("assistant", event.transcript, { partial: false });
          }
          break;
        }
        case "error": {
          let serialized = "";
          try {
            serialized = JSON.stringify(event);
          } catch {
            serialized = String(event);
          }
          console.warn("[voice] OpenAI error event", serialized, event);
          break;
        }
      }
    };
    const connect = async () => {
      var _a, _b;
      setPhase("requesting-permission");
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false
          }
        });
      } catch (permErr) {
        if (attempt.cancelled) return;
        const described = describeMicError(permErr);
        setErrorTitle(described.title);
        setError(described.body);
        setPhase("error");
        return;
      }
      if (attempt.cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      attempt.stream = stream;
      setPhase("connecting");
      const pc2 = new RTCPeerConnection();
      attempt.pc = pc2;
      const handleConnectionStateChange = () => {
        if (attempt.cancelled) return;
        const s = pc2.connectionState;
        if (s === "connected" && !attempt.webrtcConnectedEmitted) {
          attempt.webrtcConnectedEmitted = true;
          postVoiceEvent({
            conversationId,
            events: [{ type: "webrtc.connected", t: Date.now() }],
            headersToInject: authHeadersRef.current
          });
        }
        if (s === "failed" || s === "closed" || s === "disconnected") {
          void teardownAttempt(attempt, "webrtc_drop", { postEnd: true, notifyOnEnded: true });
        }
      };
      pc2.addEventListener("connectionstatechange", handleConnectionStateChange);
      if (pc2.connectionState === "connected") handleConnectionStateChange();
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      attempt.audioEl = audioEl;
      pc2.ontrack = (e) => {
        var _a2, _b2;
        if (attempt.cancelled) return;
        audioEl.srcObject = e.streams[0];
        const audioTracks = ((_b2 = (_a2 = e.streams[0]) == null ? void 0 : _a2.getAudioTracks) == null ? void 0 : _b2.call(_a2)) ?? [];
        if (audioTracks.length > 0 && !attempt.assistantStream) {
          try {
            const ms = new MediaStream();
            ms.addTrack(audioTracks[0]);
            attempt.assistantStream = ms;
            if (attempt.reachedLive) {
              setAssistantStream(ms);
            }
          } catch {
            attempt.assistantStream = e.streams[0];
            if (attempt.reachedLive) {
              setAssistantStream(e.streams[0]);
            }
          }
        }
        if (!attempt.firstAudioEmitted) {
          attempt.firstAudioEmitted = true;
          postVoiceEvent({
            conversationId,
            events: [{ type: "webrtc.first_audio", t: Date.now() }],
            headersToInject: authHeadersRef.current
          });
        }
      };
      stream.getTracks().forEach((track) => pc2.addTrack(track, stream));
      const dc = pc2.createDataChannel("oai-events");
      attempt.dc = dc;
      dc.addEventListener("message", (e) => {
        try {
          const evt = JSON.parse(e.data);
          logVoiceEvent("OPENAI", typeof (evt == null ? void 0 : evt.type) === "string" ? evt.type : "unknown", evt);
          void handleServerEvent(evt);
        } catch {
        }
      });
      try {
        authHeadersRef.current = await resolveAuthHeaders();
      } catch {
      }
      let minted;
      logVoiceEvent("BROWSER", "session request", { conversationId, pdpStacklineSku });
      try {
        minted = await mintVoiceSession({
          conversationId,
          pdpStacklineSku,
          headersToInject: authHeadersRef.current
        });
      } catch (err) {
        logVoiceEvent("BACKEND", "session response - error", {
          error: err instanceof Error ? err.message : String(err)
        });
        return void fail(err);
      }
      if (attempt.cancelled) return;
      attempt.minted = true;
      logVoiceEvent("BACKEND", "session response", minted);
      let answerSdp;
      try {
        const offer = await pc2.createOffer();
        await pc2.setLocalDescription(offer);
        const sdpResponse = await fetch(REALTIME_CALLS_URL, {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${minted.ephemeralKey}`,
            "Content-Type": "application/sdp"
          }
        });
        if (!sdpResponse.ok) {
          const txt = await sdpResponse.text().catch(() => "");
          throw new Error(`OpenAI WebRTC handshake failed (${sdpResponse.status}): ${txt.slice(0, 200)}`);
        }
        try {
          const location = sdpResponse.headers.get("Location");
          const openAiCallId = (_a = location == null ? void 0 : location.split("/").pop()) == null ? void 0 : _a.trim();
          if (openAiCallId) {
            postVoiceEvent({
              conversationId,
              events: [
                {
                  type: "webrtc.openai_call_id",
                  payload: { call_id: openAiCallId },
                  t: Date.now()
                }
              ],
              headersToInject: authHeadersRef.current
            });
          }
        } catch {
        }
        answerSdp = await sdpResponse.text();
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;
      try {
        await pc2.setRemoteDescription({ type: "answer", sdp: answerSdp });
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;
      try {
        await waitForDataChannelOpen(dc, 1e4);
      } catch (err) {
        return void fail(err);
      }
      if (attempt.cancelled) return;
      let priorItems = [];
      try {
        priorItems = await loadPriorThreadMessages({
          conversationId,
          limit: PRIOR_THREAD_REPLAY_LIMIT,
          toolLimit: PRIOR_THREAD_TOOL_REPLAY_LIMIT,
          headersToInject: authHeadersRef.current
        });
      } catch {
        priorItems = [];
      }
      if (attempt.cancelled) return;
      try {
        const { tracing: _omitTracing, ...sessionUpdate } = minted.sessionConfig;
        safeSendDataChannel(dc, { type: "session.update", session: sessionUpdate });
        let totalChars = 0;
        let priorTextCount = 0;
        let priorToolCount = 0;
        for (const item of priorItems) {
          if (item.kind === "text") {
            totalChars += item.text.length;
            priorTextCount += 1;
            safeSendDataChannel(dc, {
              type: "conversation.item.create",
              item: {
                type: "message",
                role: item.role,
                content: [{ type: item.role === "user" ? "input_text" : "output_text", text: item.text }]
              }
            });
          } else {
            totalChars += item.argsJson.length + item.output.length;
            priorToolCount += 1;
            safeSendDataChannel(dc, {
              type: "conversation.item.create",
              item: {
                type: "function_call",
                call_id: item.callId,
                name: item.toolName,
                arguments: item.argsJson
              }
            });
            safeSendDataChannel(dc, {
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: item.callId,
                output: item.output
              }
            });
          }
        }
        if (priorItems.length > 0) {
          safeSendDataChannel(dc, {
            type: "response.create",
            response: { instructions: CONTEXT_AWARE_ACK_INSTRUCTIONS }
          });
          console.info(
            `[voice] primed N=${priorTextCount} text + ${priorToolCount} tool round-trips; using context-aware acknowledgment`
          );
          void postVoiceEvent({
            conversationId,
            events: [
              {
                type: "webrtc.priming_completed",
                t: Date.now(),
                payload: {
                  priorMessageCount: priorTextCount,
                  priorToolCallCount: priorToolCount,
                  totalChars
                }
              }
            ],
            headersToInject: authHeadersRef.current
          });
        } else {
          safeSendDataChannel(dc, {
            type: "response.create",
            response: {
              instructions: `Say exactly and only this sentence: "${minted.greeting}". Do not add anything else. Do not list what you can help with. Do not say any other words.`
            }
          });
          console.info("[voice] no prior messages; using standard greeting");
        }
        const bargeIn = new VoiceBargeInController();
        attempt.bargeIn = bargeIn;
        await bargeIn.start({
          conversationId,
          getStream: () => attempt.stream,
          getAudioEl: () => attempt.audioEl,
          getDataChannel: () => attempt.dc,
          isAssistantAudible: () => isAssistantAudibleRef.current,
          isResponseInFlight: () => attempt.responseInFlight,
          getAuthHeaders: () => authHeadersRef.current,
          sendDataChannel: (event) => safeSendDataChannel(attempt.dc, event),
          onInitFailed: () => {
            safeSendDataChannel(attempt.dc, buildServerBargeInFallbackSessionUpdate());
          },
          log: (category, payload) => logVoiceEvent("VAD", category, payload)
        });
      } catch {
      }
      const bearerRaw = authHeadersRef.current.Authorization ?? authHeadersRef.current.authorization;
      const accessToken = (bearerRaw == null ? void 0 : bearerRaw.replace(/^Bearer\s+/i, "").trim()) || void 0;
      const visitorId = ((_b = authHeadersRef.current["x-bc-visitor-id"]) == null ? void 0 : _b.trim()) || void 0;
      const es = streamVoiceEventChannel({ conversationId, accessToken, visitorId });
      attempt.eventSource = es;
      es.addEventListener("error", () => {
        console.warn("[voice] event channel error");
      });
      es.onmessage = (msg) => {
        var _a2;
        if (attempt.cancelled) return;
        try {
          const evt = JSON.parse(msg.data);
          if (typeof (evt == null ? void 0 : evt.type) !== "string") return;
          if (evt.type === "data-status") {
            const statusEvt = {
              type: "data-status",
              data: { status: typeof ((_a2 = evt.data) == null ? void 0 : _a2.status) === "string" ? evt.data.status : "Thinking…" }
            };
            setLastStatusEvent(statusEvt);
            if (statusClearTimerRef.current !== void 0) {
              clearTimeout(statusClearTimerRef.current);
            }
            statusClearTimerRef.current = setTimeout(() => {
              setLastStatusEvent(void 0);
              statusClearTimerRef.current = void 0;
            }, 1e4);
          }
        } catch {
        }
      };
      attempt.liveStartMs = Date.now();
      attempt.reachedLive = true;
      setPhase("live");
      setSeconds(0);
      setPc(pc2);
      setUserStream(stream);
      setLiveStartMs(attempt.liveStartMs);
      if (attempt.assistantStream) {
        setAssistantStream(attempt.assistantStream);
      }
      attempt.ticker = setInterval(() => {
        if (attempt.cancelled || !attempt.liveStartMs) return;
        setSeconds(Math.floor((Date.now() - attempt.liveStartMs) / 1e3));
      }, 1e3);
      attempt.heartbeat = setInterval(() => {
        if (attempt.cancelled) return;
        postVoiceEvent({
          conversationId,
          events: [{ type: "browser.heartbeat", t: Date.now() }],
          headersToInject: authHeadersRef.current
        });
      }, HEARTBEAT_INTERVAL_MS);
    };
    void connect();
    return () => {
      void teardownAttempt(attempt, "unmount", {
        postEnd: attempt.minted,
        notifyOnEnded: attempt.reachedLive
      });
    };
  }, [conversationId, attemptNonce]);
  useVoiceRecorder({
    conversationId,
    userStream,
    assistantStream,
    enabled: phase === "live"
  });
  useWebRtcStats({
    conversationId,
    pc,
    enabled: phase === "live"
  });
  useEffect(() => {
    const handler = () => {
      const a = activeAttemptRef.current;
      if (!a) return;
      void teardownAttempt(a, "tab_close", { postEnd: a.minted, notifyOnEnded: false });
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [teardownAttempt]);
  const setMuted = useCallback((next) => {
    setMutedState(next);
    mutedRef.current = next;
    const a = activeAttemptRef.current;
    if (!(a == null ? void 0 : a.stream)) return;
    a.stream.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
  }, []);
  const end = useCallback(
    async (reason) => {
      const a = activeAttemptRef.current;
      if (!a) return;
      setPhase("ending");
      await teardownAttempt(a, reason ?? "user_ended", {
        postEnd: a.minted,
        notifyOnEnded: a.reachedLive
      });
    },
    [teardownAttempt]
  );
  const retry = useCallback(() => {
    setError(void 0);
    setErrorTitle(void 0);
    setLines([]);
    setWidgetEvents([]);
    setLastWidgetEvent(void 0);
    setPhase("idle");
    setAttemptNonce((n) => n + 1);
  }, []);
  const notifyLoginCompleted = useCallback(async () => {
    const a = activeAttemptRef.current;
    const ctx = loginContextRef.current;
    const blockedToolName = ctx.blockedToolName;
    const fresh = await resolveAuthHeaders().catch(() => ({}));
    authHeadersRef.current = { ...authHeadersRef.current, ...fresh };
    void postVoiceAuthPromote({
      conversationId,
      durationMs: ctx.loginGateMs ? Date.now() - ctx.loginGateMs : void 0,
      blockedToolName,
      headersToInject: authHeadersRef.current
    });
    setLastWidgetEvent({ type: "data-widget-clear", data: {} });
    loginContextRef.current = {};
    if (a) {
      const toolChoice = blockedToolName ? { type: "function", name: blockedToolName } : "auto";
      const instructions = blockedToolName ? `The shopper just finished signing in. Immediately call \`${blockedToolName}\` again with the SAME arguments as before. Do NOT explain. Do NOT apologize. Do NOT re-greet. Do NOT say you can't help — you CAN now, they're signed in. Speak only after the tool returns.` : "The shopper just finished signing in. Immediately call the tool you were going to call before the login prompt, with the same arguments. Do NOT explain or apologize — the user is signed in now. Speak only after the tool returns.";
      sendOrQueueResponseCreate(a, {
        type: "response.create",
        response: {
          instructions,
          tool_choice: toolChoice
        }
      });
    }
  }, [conversationId, sendOrQueueResponseCreate]);
  const sendText = useCallback(
    (raw) => {
      const text = raw.trim();
      if (!text) return;
      const a = activeAttemptRef.current;
      if (!(a == null ? void 0 : a.dc) || a.dc.readyState !== "open" || phase !== "live") return;
      if (a.responseInFlight) {
        safeSendDataChannel(a.dc, { type: "response.cancel" });
      }
      safeSendDataChannel(a.dc, {
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text }]
        }
      });
      sendOrQueueResponseCreate(a, { type: "response.create" });
      partialRef.current.user = void 0;
      upsertLine("user", text, { partial: false });
    },
    [phase, safeSendDataChannel, sendOrQueueResponseCreate, upsertLine]
  );
  const notifyLoginFailed = useCallback(
    (reason) => {
      const a = activeAttemptRef.current;
      if (!a) return;
      const safeReason = (reason || "verification failed").replace(/["\n]/g, " ").slice(0, 200);
      sendOrQueueResponseCreate(a, {
        type: "response.create",
        response: {
          instructions: `The verification code didn't match (reason: ${safeReason}). Gently tell the user the code was wrong and ask them to read the six digits again — they can still see the OTP form on screen. Do NOT re-prompt for the phone number, do NOT call any tools yet. Wait for the user to read the digits aloud.`
        }
      });
    },
    [sendOrQueueResponseCreate]
  );
  return {
    phase,
    muted,
    seconds,
    lines,
    error,
    errorTitle,
    isAssistantSpeaking,
    isAssistantAudible,
    isUserSpeaking,
    toolCallsInFlight,
    lastWidgetEvent,
    widgetEvents,
    lastStatusEvent,
    setMuted,
    end,
    retry,
    notifyLoginCompleted,
    notifyLoginFailed,
    sendText
  };
}
function describeMicError(err) {
  const name = err instanceof Error ? err.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return {
        title: "Microphone access needed",
        body: "Voice mode needs your microphone. Allow microphone access for this site in your browser settings, then try again."
      };
    case "NotFoundError":
    case "OverconstrainedError":
      return {
        title: "No microphone found",
        body: "We couldn't find a microphone. Connect one, then try again."
      };
    case "NotReadableError":
    case "AbortError":
      return {
        title: "Microphone unavailable",
        body: "Your microphone is unavailable — it may be in use by another app. Close anything else using it, then try again."
      };
    default:
      return {
        title: "Microphone unavailable",
        body: "We couldn't start your microphone. Please try again."
      };
  }
}
function waitForDataChannelOpen(dc, timeoutMs) {
  if (dc.readyState === "open") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("Data channel open timeout"));
    }, timeoutMs);
    const onOpen = () => {
      clearTimeout(timer);
      dc.removeEventListener("open", onOpen);
      resolve();
    };
    dc.addEventListener("open", onOpen);
  });
}
const VOICE_LOG_COLORS = {
  OPENAI: "#10B981",
  BROWSER: "#3B82F6",
  BACKEND: "#8B5CF6",
  VAD: "#F59E0B"
};
function fmtVoiceLogTimestamp(d = /* @__PURE__ */ new Date()) {
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  const h12 = d.getHours() % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  let tz = "";
  try {
    const parts = d.toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ");
    tz = parts[parts.length - 1] ?? "";
  } catch {
    tz = "";
  }
  return `${hh}:${mm}:${ss}:${ms} ${ampm}${tz ? ` ${tz}` : ""}`;
}
function logVoiceEvent(direction, category, payload) {
  let enabled = false;
  try {
    enabled = typeof localStorage !== "undefined" && localStorage.getItem("logVoiceChat") === "true";
  } catch {
    return;
  }
  if (!enabled) return;
  const header = `${direction} - ${category}`;
  const bodyObj = payload && typeof payload === "object" ? { ...payload, timestamp: fmtVoiceLogTimestamp() } : { value: payload, timestamp: fmtVoiceLogTimestamp() };
  let body;
  try {
    body = JSON.stringify(bodyObj, null, 2);
  } catch {
    body = "[unserializable payload]";
  }
  const headerStyle = `color: ${VOICE_LOG_COLORS[direction]}; font-weight: 700`;
  const bodyStyle = "color: inherit; font-weight: 400; font-family: ui-monospace, Menlo, Consolas, monospace";
  console.log(`%c${header}%c
${body}`, headerStyle, bodyStyle);
}
export {
  useVoiceSession
};
//# sourceMappingURL=useVoiceSession.js.map
